import {
  Reducer,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import {
  Box,
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogPositioner,
  DialogRoot,
  Flex,
  HStack,
  Portal,
  Spinner,
  Text,
  VStack,
  chakra,
} from "@chakra-ui/react";
import Cropper, { Area, Point } from "react-easy-crop";
import { ProfilePictureKey } from "@greenflagdate/shared";
import { Icon } from "../../../components/Icon/Icon";
import { convertHeicToJpeg } from "../../../lib/convertHeicToJpeg";
import { PictureData, pictureMeta } from "../ProfileEditor/pictureMeta";

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

const ACCEPTED_MIME = "image/jpeg,image/png,image/heic,image/heif";
const MAX_FILE_BYTES = 10_000_000; // 10MB

interface PictureEditorProps {
  pictureType?: ProfilePictureKey;
  isShown: boolean;
  onSave: (data: PictureData) => void;
  onClose: () => void;
  onDelete: () => void;
  blob?: Blob;
  cropArea?: Area;
}

interface PictureEditorState {
  converting: boolean;
  cropPoint?: Point;
  zoom?: number;
  blobOverride?: Blob | null;
  uploadError?: string;
}

const shallowEqual = (a: unknown, b: unknown) => {
  if (Object.is(a, b)) return true;
  if (
    typeof a !== "object" ||
    a === null ||
    typeof b !== "object" ||
    b === null
  )
    return false;
  const aKeys = Object.keys(a as object);
  const bKeys = Object.keys(b as object);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (
      !Object.is(
        (a as Record<string, unknown>)[k],
        (b as Record<string, unknown>)[k],
      )
    )
      return false;
  }
  return true;
};

const reducer: Reducer<PictureEditorState, Partial<PictureEditorState>> = (
  state,
  merge,
) => {
  let changed = false;
  for (const k of Object.keys(merge) as (keyof PictureEditorState)[]) {
    if (!shallowEqual(state[k], merge[k])) {
      changed = true;
      break;
    }
  }
  return changed ? { ...state, ...merge } : state;
};

export const PictureEditor = ({
  pictureType: key,
  isShown,
  onSave,
  onClose,
  onDelete,
  blob,
  cropArea,
}: PictureEditorProps) => {
  const [
    { converting, blobOverride, cropPoint, zoom, uploadError },
    dispatch,
  ] = useReducer(reducer, {
    converting: false,
  });

  const currentCropArea = useRef<Area>({ x: 0, y: 0, width: 100, height: 100 });
  const finalImageSrc = useMemo(() => {
    if (blobOverride === null) {
      return null;
    }
    const calcBlob = blobOverride || blob;
    if (!calcBlob) return null;
    return URL.createObjectURL(calcBlob);
  }, [blob, blobOverride]);

  const page: "select" | "loading" | "crop" = finalImageSrc
    ? "crop"
    : converting
      ? "loading"
      : "select";

  const close = useCallback(() => {
    dispatch({
      converting: false,
      blobOverride: undefined,
      cropPoint: undefined,
      zoom: undefined,
      uploadError: undefined,
    });
    onClose();
  }, [onClose]);

  const del = useCallback(() => {
    dispatch({
      converting: false,
      cropPoint: undefined,
      zoom: undefined,
      blobOverride: null,
    });
    onDelete();
  }, [onDelete]);

  useEffect(() => {
    if (!isShown) {
      dispatch({
        converting: false,
        blobOverride: undefined,
        cropPoint: undefined,
        zoom: undefined,
        uploadError: undefined,
      });
    }
  }, [isShown]);

  useEffect(() => {
    dispatch({
      converting: false,
      blobOverride: undefined,
      cropPoint: undefined,
      zoom: undefined,
      uploadError: undefined,
    });
  }, [key]);

  const onPickFile = useCallback(async (file?: File) => {
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      dispatch({ uploadError: "That photo is over 10MB. Please choose a smaller one." });
      return;
    }
    dispatch({ converting: true, uploadError: undefined });
    try {
      const converted = await convertHeicToJpeg(file);
      dispatch({ blobOverride: converted });
    } catch {
      dispatch({
        converting: false,
        uploadError: "We couldn't read that photo. Try a different file.",
      });
    }
  }, []);

  return (
    <DialogRoot
      open={isShown}
      onOpenChange={(details) => {
        if (!details.open) close();
      }}
      size="md"
      placement="center"
      motionPreset="slide-in-bottom"
    >
      <Portal>
      <DialogBackdrop zIndex={2000} />
      <DialogPositioner zIndex={2001}>
        <DialogContent
          w={{ base: "calc(100% - 2rem)", md: "32rem" }}
          maxW="32rem"
          mx="auto"
          maxH="calc(100vh - 4rem)"
          borderRadius="md"
          bg="bg.canvas"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <DialogBody
            p="6"
            minH={0}
            overflowY="auto"
            display="flex"
            flexDirection="column"
          >
            {page === "loading" && (
              <Flex minH="200px" align="center" justify="center">
                <Spinner size="lg" color="fg.brand" />
              </Flex>
            )}

            {page === "select" && key && (
              <VStack align="stretch" gap="4">
                <VStack align="stretch" gap="2">
                  {pictureMeta[key].images.map((i) => (
                    <Box
                      key={i.src}
                      position="relative"
                      w="100%"
                      style={{ aspectRatio: "718 / 423" }}
                      backgroundImage={`url(${i.src})`}
                      backgroundSize="cover"
                      backgroundPosition="center"
                      borderRadius="xs"
                    >
                      <Flex
                        position="absolute"
                        top="7%"
                        right="7%"
                        w="28px"
                        h="28px"
                        borderRadius="full"
                        bg={i.type === "good" ? "bg.brand" : "bg.danger"}
                        color="white"
                        align="center"
                        justify="center"
                        boxShadow="0 2px 4px rgba(0,0,0,0.25)"
                      >
                        <Icon
                          name={i.type === "good" ? "check" : "x"}
                          boxSize="14px"
                        />
                      </Flex>
                    </Box>
                  ))}
                </VStack>
                <Box>
                  <Text fontSize="18px" fontWeight="bold" color="fg.default" mb="2">
                    {pictureMeta[key].title}
                  </Text>
                  <Text fontSize="13px" color="fg.muted" lineHeight="1.5">
                    {pictureMeta[key].description}
                  </Text>
                </Box>
                {uploadError && (
                  <Text fontSize="12px" color="fg.danger">
                    {uploadError}
                  </Text>
                )}
              </VStack>
            )}

            {page === "crop" && (
              <Box
                position="relative"
                w="100%"
                style={{ aspectRatio: "1 / 1" }}
                bg="black"
                borderRadius="xs"
                overflow="hidden"
              >
                <chakra.button
                  type="button"
                  onClick={del}
                  aria-label="Remove photo"
                  position="absolute"
                  top="10px"
                  right="10px"
                  zIndex={2}
                  w="36px"
                  h="36px"
                  borderRadius="full"
                  bg="bg.danger"
                  color="white"
                  border="0"
                  cursor="pointer"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  _hover={{ filter: "brightness(1.05)" }}
                >
                  <Icon name="x" boxSize="14px" />
                </chakra.button>
                <Cropper
                  key={`${key ?? "none"}:${finalImageSrc ?? ""}`}
                  image={finalImageSrc!}
                  crop={cropPoint || { x: 0, y: 0 }}
                  zoom={zoom ?? 1}
                  aspect={1}
                  initialCroppedAreaPercentages={cropArea}
                  onCropChange={(nextCropPoint) =>
                    dispatch({ cropPoint: nextCropPoint })
                  }
                  onCropComplete={(nextCropArea) => {
                    currentCropArea.current = nextCropArea;
                  }}
                  onZoomChange={(nextZoom) => dispatch({ zoom: nextZoom })}
                  minZoom={MIN_ZOOM}
                  maxZoom={MAX_ZOOM}
                />
              </Box>
            )}
          </DialogBody>

          <DialogFooter
            borderTop="1px solid"
            borderColor="border.subtle"
            bg="bg.canvas"
            p="4"
          >
            <HStack w="100%" gap="2">
              <chakra.button
                type="button"
                onClick={close}
                flex="1"
                h="44px"
                bg="transparent"
                color="fg.muted"
                border="1px solid"
                borderColor="border.default"
                borderRadius="pill"
                fontWeight="semibold"
                fontSize="14px"
                cursor="pointer"
                _hover={{ bg: "bg.subtle" }}
              >
                Cancel
              </chakra.button>

              {page === "select" && (
                <chakra.label
                  flex="2"
                  h="44px"
                  bg="bg.brand"
                  color="fg.onBrand"
                  borderRadius="pill"
                  fontWeight="bold"
                  fontSize="14px"
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer"
                  px="4"
                  _hover={{ filter: "brightness(1.04)" }}
                >
                  Choose Photo
                  <chakra.input
                    type="file"
                    accept={ACCEPTED_MIME}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      void onPickFile(f);
                      e.target.value = "";
                    }}
                    position="absolute"
                    w="1px"
                    h="1px"
                    opacity={0}
                    overflow="hidden"
                    style={{ clip: "rect(0 0 0 0)" }}
                  />
                </chakra.label>
              )}

              {page === "loading" && (
                <Flex
                  flex="2"
                  h="44px"
                  align="center"
                  justify="center"
                  bg="bg.subtle"
                  borderRadius="pill"
                >
                  <Spinner size="sm" color="fg.brand" />
                </Flex>
              )}

              {page === "crop" && (
                <chakra.button
                  type="button"
                  onClick={() =>
                    onSave({
                      cropArea: currentCropArea.current,
                      imageSrc: finalImageSrc!,
                      blob: (blobOverride || blob)!,
                    })
                  }
                  flex="2"
                  h="44px"
                  bg="bg.brand"
                  color="fg.onBrand"
                  border="0"
                  borderRadius="pill"
                  fontWeight="bold"
                  fontSize="14px"
                  cursor="pointer"
                  _hover={{ filter: "brightness(1.04)" }}
                >
                  Save Photo
                </chakra.button>
              )}
            </HStack>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
      </Portal>
    </DialogRoot>
  );
};
